'use client';

import { useClientContext } from '@dealerportal/react-helpers';
import Script from 'next/script';
import { useRef } from 'react';
import ReCaptcha from 'react-google-recaptcha';

type Props = {
  onclick?: string;
  formId: string;
  [key: string]: any;
};

/** True once any registration for this form reached process.js's arrays. */
const isRegistered = (formId: string): boolean => {
  const prefix = `${formId}_`;
  const ids: unknown[] = [
    ...(window.requiredFields ?? []),
    ...(window.requiredFieldGroups ?? []).map((group) => group[0]),
    ...(window.validatedFields ?? []).map((field) => field[0]),
  ];
  return ids.some((id) => typeof id === 'string' && id.startsWith(prefix));
};

/**
 * The CMS field-registration snippet
 * (`if (typeof (addFieldToValidate) != "undefined") { addFieldToValidate("form_0025_fld_2", "EMAIL"); }`)
 * is emitted inline and runs at hydration, before the asynchronously fetched
 * process.js defines that function — so its `typeof` guard is false and every
 * registration is dropped, leaving doSubmit() with nothing to validate
 * (#3093). Re-running the snippet on click fixes that: by then process.js has
 * loaded, including the CMS-supplied duplicate copy whose late arrival resets
 * `validatedFields`. The snippet is a list of guarded registration calls, so
 * running it in function scope is equivalent to the browser's own execution.
 */
export const registerActonFields = (formId: string): void => {
  if (isRegistered(formId)) return;
  const source = document.getElementById('acton-validation')?.textContent;
  if (source == null || source === '') return;
  new Function(source)();
};

export const ActonSubmitButton = ({ formId, onclick, ...rest }: Props) => {
  const recaptcha = useClientContext().recaptcha;
  const recaptchaRef = useRef<ReCaptcha>(null);

  const recaptchaEnabled = recaptcha.Enabled && recaptcha.SiteKey != null;
  return (
    <>
      <Script src="/js/process.js" strategy="afterInteractive" />
      {recaptchaEnabled && (
        <ReCaptcha
          ref={recaptchaRef}
          sitekey={recaptcha.SiteKey!}
          className="g-recaptcha"
          style={{ maxWidth: '304px', margin: '10px auto' }}
        />
      )}
      <input
        {...rest}
        onClick={(): void => {
          registerActonFields(formId);

          if (recaptchaEnabled) {
            const recaptchaValue = recaptchaRef.current?.getValue();
            if (recaptchaValue == null || recaptchaValue === '') {
              alert(`Please select "I'm not a robot" to proceed`);
              return;
            }
          }

          switch (onclick) {
            case 'validateForm()':
              validateForm();
              break;
            case `doSubmitCamp(document.getElementById('${formId}'))`:
              doSubmitCamp(document.getElementById(formId));
              break;
            default:
              doSubmit(document.getElementById(formId));
          }
        }}
      />
    </>
  );
};

export default ActonSubmitButton;
